import React from 'react';
import { IPageName, getPath } from '@secretescapes/cauldron';
import {
  Device,
  Icon,
  IconName,
  Typography,
  TypographyVariant,
  isIconName,
  useDeviceViewport,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useApplicationState } from '@utils/useApplicationState';
import { useTranslations } from '@utils/useTranslations';
import { UncontrolledDetails } from '@features/Sale/Details/UncontrolledDetails';
import { type FacilitiesPresentationLayers } from './Facilities.interfaces';
import {
  Container,
  GroupedFacilitiesSectionsFlexbox,
  Section,
  StyledList,
  StyledModalList,
  TypographyIconFlexbox,
  TypographyWithLink,
} from './Facilities.styles';
import { Facility, FacilityVariant } from './Facility';

export const ACCESSIBILITY_DISCLAIMER_TEST_ID = 'accessibility-disclaimer';
export const SEE_ALL_FACILITIES_CONTENT_TEST_ID = 'see-all-faciltiies-content';
export const TOP_FACILITIES_DISPLAY_TEST_ID = 'top-facilities-display';
export const GROUPED_FACILITIES_DISPLAY_TEST_ID = 'grouped-facilities-display';

interface FacilitiesProps {
  facilities: FacilitiesPresentationLayers;
}

export const Facilities = ({ facilities }: FacilitiesProps) => {
  const { territoryCode } = useApplicationState();
  const { translations } = useTranslations();
  const { flattenedFacilities, groupedFacilities, accessibilityFacility } = facilities;
  const device = useDeviceViewport();

  const isMobile = device === Device.mobile;

  const initialFacilitiesToDisplay = flattenedFacilities.slice(0, 12);

  const generalAccessibilityDisclaimer = i18n(
    'disclaimer_accessibility_general',
    [getPath(IPageName.contactUs, territoryCode) ?? ''],
    translations
  );

  const accessibilityDisclaimer = accessibilityFacility?.name
    ? `${accessibilityFacility.name} ${generalAccessibilityDisclaimer}`
    : generalAccessibilityDisclaimer;

  return (
    <Container isMobile={isMobile}>
      <StyledList data-testid={TOP_FACILITIES_DISPLAY_TEST_ID} isMobile={isMobile}>
        {initialFacilitiesToDisplay.map(facility => (
          <Facility key={facility.key} facility={facility} />
        ))}
      </StyledList>
      <UncontrolledDetails
        buttonLabel={i18n('sale.page.facilities.see.all.link', [], translations)}
        popupTitle={i18n('sales.filter.facility.label', [], translations)}
        content={
          <GroupedFacilitiesSectionsFlexbox data-testid={SEE_ALL_FACILITIES_CONTENT_TEST_ID}>
            {groupedFacilities.map(facilityGroup => (
              <Section key={facilityGroup.key}>
                <TypographyIconFlexbox>
                  <Icon
                    name={
                      isIconName(facilityGroup.key) ? IconName[facilityGroup.key] : IconName.general
                    }
                  />
                  <Typography variant={TypographyVariant.st2}>{facilityGroup.name}</Typography>
                </TypographyIconFlexbox>
                {facilityGroup.facts?.length && (
                  <StyledModalList data-testid={GROUPED_FACILITIES_DISPLAY_TEST_ID}>
                    {facilityGroup.facts.map(facility => (
                      <Facility
                        key={facility?.key}
                        facility={facility!}
                        variant={FacilityVariant.Detailed}
                      />
                    ))}
                  </StyledModalList>
                )}
              </Section>
            ))}
          </GroupedFacilitiesSectionsFlexbox>
        }
      />
      <Typography variant={TypographyVariant.b3}>
        {i18n('disclaimer_facilities_general', [], translations)}
      </Typography>
      <TypographyWithLink
        dataAttributes={{ testid: ACCESSIBILITY_DISCLAIMER_TEST_ID }}
        variant={TypographyVariant.b3}
        allowDangerousRender={true}
      >
        {accessibilityDisclaimer}
      </TypographyWithLink>
    </Container>
  );
};
